import AddToCalendar from '@liveauctioneers/caterwaul-components/lib/AddToCalendar/AddToCalendar';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import { RequestOnlineBiddingButton } from '@/components/ItemBidding/RequestOnlineBiddingButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogDescription,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogDetailExternalUrl } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { submitRegisterForCatalog, SubmitRegisterForCatalogParams } from '@/redux/modules/registerForCatalog';
import { useTrackAddToCalendarPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackAddToCalendarPressed.analytics';
import { calendarEvent } from '@/utils/calendarEvent';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { CatalogRegistrationMessages } from './CatalogRegistrationMessages';

type Props = {
    catalogId: number;
    className?: string;
};

export const CatalogOnlyAuctionStatus = ({ catalogId, className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const externalUrl = useAppSelector((state) => getCatalogDetailExternalUrl(state, catalogId));
    const trackAddToCalendarPressedAnalytics = useTrackAddToCalendarPressedAnalytics();

    const registerForCatalog = useCallback(
        ({ catalogId, itemId }: SubmitRegisterForCatalogParams) =>
            dispatch(submitRegisterForCatalog({ catalogId, itemId })),
        [dispatch]
    );

    const trackAddToCalendar = (calendar: string, calendarType: string) => {
        trackAddToCalendarPressedAnalytics({ calendarType });
    };

    const event = calendarEvent({
        catalogId,
        description,
        saleStartTs,
        sellerName,
        title,
    });

    return (
        <div
            className={cn('flex h-full max-w-full flex-col items-center justify-start', className)}
            data-testid="catalog-only-auction-status"
        >
            <CatalogRegistrationMessages catalogId={catalogId} />
            <RequestOnlineBiddingButton
                catalogId={catalogId}
                className="mb-sm w-full max-w-full"
                submitRegisterForCatalog={registerForCatalog}
                text={Boolean(externalUrl) && formatMessage(['visit_partners_site'])}
            />
            <AddToCalendar
                buttonLabel={String(formatMessage(['add_to_calendar']))}
                buttonWrapperClass="mb-0 w-full [&>button]:mb-0 [&>button]:w-full"
                className="relative w-full max-w-full"
                event={event}
                onClick={trackAddToCalendar}
            />
        </div>
    );
};
